/**
 * EmailReveal — accessible click-to-reveal for the public email address.
 *
 * Before the user activates the button, no complete address exists in the
 * DOM, in an href, in aria attributes or in the markup. On explicit
 * activation (mouse, touch or keyboard) the address is reassembled from
 * encoded fragments, announced to screen readers, and rendered as a working
 * mailto link with an optional copy action.
 *
 * This discourages simple harvesting only; it is not bot-proof.
 */

import { useCallback, useState } from "react";
import { Mail, Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { revealEmail } from "@/utils/emailProtection";
import { cn } from "@/utils/cn";

interface EmailRevealProps {
  /** Visual treatment: light text on dark background, or dark text on light */
  tone?: "light" | "dark";
  /** Size of the revealed address */
  size?: "sm" | "lg";
  className?: string;
  /** Show the copy-to-clipboard action once revealed */
  withCopy?: boolean;
}

export const EmailReveal = ({
  tone = "dark",
  size = "sm",
  className,
  withCopy = true,
}: EmailRevealProps) => {
  const { t } = useTranslation();
  const [address, setAddress] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleReveal = useCallback(() => {
    setAddress(revealEmail());
  }, []);

  const handleCopy = useCallback(async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [address]);

  const light = tone === "light";

  const buttonClass = cn(
    "inline-flex items-center gap-2 border px-4 py-2.5 text-sm font-bold uppercase tracking-[0.1em] transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
    light
      ? "border-white/40 text-white hover:bg-white/10 focus-visible:outline-white"
      : "border-foreground/30 text-foreground hover:bg-muted focus-visible:outline-cobalt",
  );

  const addressClass = cn(
    "font-extrabold tracking-tight break-all transition-colors duration-200",
    size === "lg" ? "text-xl md:text-3xl" : "text-base md:text-lg",
    light ? "text-white hover:text-cobalt-bright" : "text-foreground hover:text-cobalt",
  );

  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      <p aria-live="polite" className="sr-only">
        {address ? `${t("email.revealedAnnounce")} ${address}` : ""}
      </p>

      {address === null ? (
        <button type="button" onClick={handleReveal} className={buttonClass}>
          <Mail className="h-4 w-4" aria-hidden="true" />
          {t("email.revealLabel")}
        </button>
      ) : (
        <>
          <a href={`mailto:${address}`} className={addressClass}>
            {address}
          </a>
          {withCopy && (
            <button
              type="button"
              onClick={handleCopy}
              className={cn(
                "inline-flex items-center gap-2 border px-3 py-1.5 text-xs font-bold uppercase tracking-[0.1em] transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                light
                  ? "border-white/40 text-white/80 hover:bg-white/10 focus-visible:outline-white"
                  : "border-foreground/25 text-muted-foreground hover:bg-muted focus-visible:outline-cobalt",
              )}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {copied ? t("email.copied") : t("email.copy")}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default EmailReveal;
